/**
 * Mergeport Ordering
 * ## Introduction    Mergeport Ordering is a channel manager for online ordering and delivery services. With a single interface, we establish a connection between a network of POS systems and multiple service providers in a standardized format.    ## Access details and developer account   If not already done, **contact us** for a developer account. You will receive a ```providerId``` as identification of your platform as well as a ```restaurantId``` of  demo restaurant. In addition, we will provide you with credentials to log in to our MERGEPORT Controller interface. The ```restaurantId``` changes for each restaurant.     ## Data flow   MERGEPORT provides the possibility to synchronize orders and articles with a POS system. The POS system linked to the specified ```restaurantId``` will be notified in realtime about incoming orders or cancellations and process them correspondingly. MERGEPORT will deliver the orders also if the POS system is not available at the time of creation.    ### Order status  Once the order has been dispatched, the POS can process it. First, the POS will set the status to ```fetchedByPOS``` to mark it as read. After processing, the POS is supposed to update the status to ```acceptedByPOS``` or ```rejectedByPOS```. ```acceptedByPOS``` means, the gastronomy will take care about the delivery. You can check the status by either fetching the order via the HTTP GET endpoint or by setting a web hook for the notification. Please contact the MERGEPORT admins upon provider creation for this feature or use the corresponding endpoint to set the web hook (callbackUrl) via API..     ### Cancellation   Should the order be cancelled before the POS processes the order, the order will be terminated and not forwarded to the POS anymore. If the POS has already started the processing, it will be assigned with a request to cancel the order.    ### Payment   What is specified in the ```payment``` object of the payment defines how the POS handles the order. The parameter ```payOnDelivery``` defines whether the order is already paid (```payOnDelivery=false```) or still needs to be paid (```payOnDelivery=true```). The ```brand```attribute states where the payment came from, e.g. paypal and the ```type``` specifies if it is an online (```CARD```) or offline (```CASH```) payment.    ### Prices and currencies   All prices are expressed as a structured object, with the currency code, e.g. \"EUR\" and the value in the atomic part of that currency, typically \"cent\". 3� will be represented as 300.    ### Menu, categories and items   Depending on the implementation of the POS, MERGEPORT stores a shadow copy of the products (internally also called ```items```), menu categories of the POS. The POS is supposed to update the products at MERGEPORT upon every change. Within the MERGEPORT controller, the gastronomer is able to enrich the products, menu and categories with meta information such as localized names and descriptions as well as images. By requesting the menu of the restaurant, the provider can import the (enriched) products. Almost any kind of variation of products can be represented by this structure. The ```type``` of a product distinguishes products and toppings. Both of which can be associated to menus and categories.  With the parameter ```parentIds```, products/toppings can be linked to one or multiple items in a hierarchical order.  To post an order, the provider needs to set the item ID of the product as the ```remoteCode``` of the product (see POST Order). The products of an order are included in the ```products``` object in a hierarchical order. For example, toppings can be included as a child of the parent product.    ### Matching of the products   When an order is placed, the POS will try to match the contained products with the articles stored in its database. If a ```remoteCode``` is set by the platform, the POS will match it against its interal product ids. If this process fails, it will try to match a product by name or create a dummy fallback product. Additionally, if the implementation allows it, the gastronomer is able to map the products of the POS with those of the platform in the MERGEPORT Controller.  ## Responses   Typically, our responses are being returned with the **HTTP status codes** ```200``` **OK**, ```400``` **Bad Request**, ```401``` **Unauthorized**, ```403``` **Forbidden**. We have three response types, you can easily check the response for the following fields&#58;   - ```[data]``` - for read operations. The response contains an object or array of the requested data   - ```success``` - for generic operations. The success property contains a message describing the operation result.   - ```error``` - for minor errors. The error property contains a code and a message  ## Request validation   All requests will be validated. The following cases will lead to a validation error:   - Missing required properties   - Unknown properties in the request body   - Invalid data format.    Important&#58; date-time fields and parameters must be formatted as UTC ISO 8601 strings, e.g. '2000-01-01T10:10:10.123Z'. 
 *
 * OpenAPI spec version: 4.0.8
 * Contact: hello@mergeport.com
 *
 * NOTE: This class is auto generated by the swagger code generator program.
 * https://github.com/swagger-api/swagger-codegen.git
 * Do not edit the class manually.
 */
import { HooksmainproviderIdrestaurantIditemsOptions } from './hooksmainproviderIdrestaurantIditemsOptions';

export interface InlineResponse2001 { 
    /**
     * site id
     */
    siteId: string;
    /**
     * item id in the pos system
     */
    posItemId: string;
    price?: any;
    /**
     * associative array of different names for ISO-639-1-Codes
     */
    name?: { [key: string]: string; };
    /**
     * associative array of different categories for ISO-639-1-Codes
     */
    category?: { [key: string]: string; };
    /**
     * associative array of different subCategories for ISO-639-1-Codes
     */
    subCategory?: { [key: string]: string; };
    /**
     * associative array of different descriptions for ISO-639-1-Codes
     */
    description?: { [key: string]: string; };
    /**
     * here you can specify the allergens  __Codes:__  - **A** - Grains containing gluten  - **B** - Crustaceans  - **C** - Egg  - **D** - Fish  - **E** - Peanuts  - **F** - Soy  - **G** - Milk or lactose  - **H** - Edible nuts  - **L** - Celery  - **M** - Mustard  - **N** - Sesame  - **O** - Sulphites  - **P** - Lupines  - **R** - Molluscs
     */
    allergens?: string;
    /**
     * extra unique reference of the item (PLU)
     */
    reference?: string;
    /**
     * item id of the parent item in the pos system
     */
    parentId?: string;
    /**
     * list of item ids of the parent items in the pos system
     */
    parentIds?: Array<string>;
    /**
     * url to the image of the product
     */
    imgUrl?: string;
    /**
     * on/off switch for the product
     */
    enabled?: boolean;
    /**
     * items currently in stock
     */
    stock?: number;
    /**
     * list of category ids of the parent categories in the pos system
     */
    categoryIds?: Array<string>;
    /**
     * list of menu ids of the parent menus in the pos system
     */
    menuIds?: Array<string>;
    /**
     * list of extra labels for the product
     */
    labels?: Array<string>;
    /**
     * extra unique reference of the item (European Article Number)
     */
    ean?: string;
    /**
     * extra type definition of the product
     */
    type?: InlineResponse2001.TypeEnum;
    /**
     * number to sort items
     */
    sort?: number;
    options?: HooksmainproviderIdrestaurantIditemsOptions;
}
export namespace InlineResponse2001 {
    export type TypeEnum = 'product' | 'topping';
    export const TypeEnum = {
        Product: 'product' as TypeEnum,
        Topping: 'topping' as TypeEnum
    };
}